import { c as _c } from "react/compiler-runtime";
import './feature-base.scss';
import * as z from 'zod';
import type { ElementType } from 'react';
import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { LogoChooser } from '@sola/ark-core/atoms/logos/logos.tsx';
import { Prominence, Sizes, Text, Variants } from '@sola/ark-core/atoms/text/text.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { IconNamesSafe } from '@sola/schema/lib/icons/icon-names.ts';
import { FlexibleContentProps } from '@sola/schema/lib/schema.ts';
import { Image } from '../../atoms/image/image.tsx';
import { Blockquote } from '../blockquote/blockquote.tsx';
import { FeatureButtons, FeatureButtonsProps } from '../feature-buttons/feature-buttons.tsx';
export const FeatureBaseProps = FlexibleContentProps.extend({
  ...FeatureButtonsProps.shape,
  icon: IconNamesSafe.optional(),
  imagePlacement: z.enum(['left', 'right']).optional(),
  titleVariant: z.enum(['heading', 'label', 'body']).optional(),
  bodyVariant: z.enum(['label', 'body']).optional(),
  buttonVariant: z.enum(['default', 'stacked', 'grow', 'noWrap']).optional(),
  variant: z.enum(['standard', 'iconRow', 'centered', 'stat', 'listItem', 'quotation', 'quotationCentered']).optional(),
  variantSpacing: z.enum(['card', 'section', 'default']).optional(),
  variantResponsive: z.boolean().optional(),
  titleSize: z.enum(['xs', 'sm', 'md', 'lg', 'xl', 'xxl']).optional(),
  bodySize: z.enum(['xs', 'sm', 'md', 'lg', 'inherit']).optional(),
  buttonSize: z.enum(['sm', 'md', 'lg', 'xl']).optional(),
  inline: z.boolean().optional(),
  className: z.string().optional(),
  isClickable: z.boolean().optional(),
  hasPlaceholder: z.boolean().optional(),
  titleAsLink: z.boolean().optional(),
  buttonsAsLinks: z.boolean().optional()
});
export type FeatureBaseProps = z.infer<typeof FeatureBaseProps>;
const getSpacingClasses = (sizeTitle: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl', sizeBody: 'xs' | 'sm' | 'md' | 'lg' | 'inherit', sizeButton: 'sm' | 'md' | 'lg' | 'xl', variantTitle: 'heading' | 'label' | 'body', variantBody: 'body' | 'label', variantSpacing: 'card' | 'section' | 'default') => {
  let classNameHeaderSpacing;
  let classNameContentSpacing;
  if (variantTitle === 'label' && variantBody === 'body') {
    classNameHeaderSpacing = 'gap-snug-xl';
    classNameContentSpacing = 'gap-comfy-lg';
    if (['xs', 'sm'].includes(sizeTitle)) {
      classNameHeaderSpacing = 'gap-comfy-sm';
    }
    if (['xs', 'sm'].includes(sizeBody)) {
      classNameContentSpacing = 'gap-comfy-md';
    }
    return {
      classNameHeaderSpacing,
      classNameContentSpacing
    };
  }
  if (variantTitle === 'label') {
    classNameHeaderSpacing = 'gap-snug-xl';
    classNameContentSpacing = 'gap-snug-xl';
    return {
      classNameHeaderSpacing,
      classNameContentSpacing
    };
  }
  const variantSpacingHeading = variantSpacing === 'card' ? '@sm' : '@lg';
  const variantSpacingContent = variantSpacing === 'card' ? '@sm' : '@md';
  classNameHeaderSpacing = `gap-comfy-xs ${variantSpacingHeading}:gap-comfy-sm`;
  classNameContentSpacing = `gap-comfy-md ${variantSpacingContent}:gap-comfy-lg`;
  if (variantSpacing === 'card' && sizeButton === 'lg') {
    classNameContentSpacing += ` @lg:gap-comfy-xl`;
  }
  classNameHeaderSpacing = 'gap-snug-xl';
  if (['xs', 'sm'].includes(sizeBody)) {
    classNameHeaderSpacing = 'gap-snug-lg';
    classNameContentSpacing = 'gap-comfy-md';
  }
  if (['xl'].includes(sizeTitle)) {
    classNameContentSpacing = `gap-comfy-lg ${variantSpacingContent}:gap-comfy-xl`;
  }
  return {
    classNameHeaderSpacing,
    classNameContentSpacing
  };
};
export const FeatureBase: React.FC<FeatureBaseProps & {
  programmaticButtons?: React.ReactNode | React.ReactNode[];
  handleTitleClick?: () => void;
}> = t0 => {
  const $ = _c(119);
  let alignment;
  let body;
  let className;
  let hasPlaceholder;
  let icon;
  let isClickable;
  let logo;
  let media;
  let programmaticButtons;
  let props;
  let quotation;
  let subtitle;
  let t1;
  let t10;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let title;
  if ($[0] !== t0) {
    ({
      seoLevel: t1,
      icon,
      media,
      subtitle,
      title,
      titleSize: t2,
      titleVariant: t3,
      body,
      bodySize: t4,
      bodyVariant: t5,
      buttonSize: t6,
      variant: t7,
      variantSpacing: t8,
      variantResponsive: t9,
      inline: t10,
      className,
      quotation,
      logo,
      programmaticButtons,
      isClickable,
      hasPlaceholder,
      alignment,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = alignment;
    $[2] = body;
    $[3] = className;
    $[4] = hasPlaceholder;
    $[5] = icon;
    $[6] = isClickable;
    $[7] = logo;
    $[8] = media;
    $[9] = programmaticButtons;
    $[10] = props;
    $[11] = quotation;
    $[12] = subtitle;
    $[13] = t1;
    $[14] = t10;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
    $[23] = title;
  } else {
    alignment = $[1];
    body = $[2];
    className = $[3];
    hasPlaceholder = $[4];
    icon = $[5];
    isClickable = $[6];
    logo = $[7];
    media = $[8];
    programmaticButtons = $[9];
    props = $[10];
    quotation = $[11];
    subtitle = $[12];
    t1 = $[13];
    t10 = $[14];
    t2 = $[15];
    t3 = $[16];
    t4 = $[17];
    t5 = $[18];
    t6 = $[19];
    t7 = $[20];
    t8 = $[21];
    t9 = $[22];
    title = $[23];
  }
  const seoLevel = t1 === undefined ? 2 : t1;
  const titleSize = t2 === undefined ? "md" : t2;
  const titleVariant = t3 === undefined ? "heading" : t3;
  const bodySize = t4 === undefined ? "md" : t4;
  const bodyVariant = t5 === undefined ? "body" : t5;
  const buttonSize = t6 === undefined ? "lg" : t6;
  const variant = t7 === undefined ? "standard" : t7;
  const variantSpacing = t8 === undefined ? "default" : t8;
  const variantResponsive = t9 === undefined ? false : t9;
  const inline = t10 === undefined ? false : t10;
  const TitleTag = `h${seoLevel}` as ElementType;
  const t11 = `${variantResponsive ? "flex-col @lg:flex-row" : "flex-row"} gap-comfy-sm`;
  const t12 = `${variantResponsive ? "@md:items-center @md:text-center" : "items-center text-center"} flex-col gap-snug-xl`;
  const t13 = `${variantResponsive ? "@md:items-center @md:text-center" : "items-center text-center"} flex-col gap-snug-xl`;
  let hasButtons;
  let hasTitleGroup;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let titleAsLink;
  if ($[24] !== bodySize || $[25] !== bodyVariant || $[26] !== buttonSize || $[27] !== className || $[28] !== hasPlaceholder || $[29] !== icon || $[30] !== inline || $[31] !== isClickable || $[32] !== logo || $[33] !== media?.image || $[34] !== programmaticButtons || $[35] !== props.primaryCTA || $[36] !== props.secondaryCTA || $[37] !== props.tertiaryCTA || $[38] !== seoLevel || $[39] !== subtitle || $[40] !== t11 || $[41] !== t12 || $[42] !== t13 || $[43] !== title || $[44] !== titleSize || $[45] !== titleVariant || $[46] !== variant || $[47] !== variantResponsive || $[48] !== variantSpacing) {
    const variantClass = {
      standard: "flex-col gap-comfy-sm text-left items-start",
      iconRow: t11,
      centered: t12,
      quotationCentered: t13,
      quotation: "flex-col gap-comfy-sm text-left items-start"
    };
    const spacingClasses = getSpacingClasses(titleSize, bodySize, buttonSize, titleVariant, bodyVariant, variantSpacing);
    titleAsLink = variant === "listItem";
    hasTitleGroup = title || subtitle;
    hasButtons = props.primaryCTA || props.secondaryCTA || props.tertiaryCTA || programmaticButtons;
    t16 = cn("FeatureBase w-full flex", `FeatureBase--${variant}`, variantClass[variant as keyof typeof variantClass], {
      "FeatureBase--inline": inline,
      "is-responsive": variantResponsive
    }, className);
    if ($[58] !== icon || $[59] !== isClickable || $[60] !== titleAsLink) {
      t17 = icon && !isClickable && !titleAsLink && <div className="FeatureBase__icon-container"><div className="FeatureBase__icon"><ArkIcon iconName={icon} size="24" className="FeatureBase__icon-svg" /></div></div>;
      $[58] = icon;
      $[59] = isClickable;
      $[60] = titleAsLink;
      $[61] = t17;
    } else {
      t17 = $[61];
    }
    if ($[62] !== logo) {
      t18 = logo && <div className="FeatureBase__logo"><LogoChooser name={logo.logo || "compassion"} color="full" className="is-full" /><LogoChooser name={logo.logo || "compassion"} color="white" className="is-white" /></div>;
      $[62] = logo;
      $[63] = t18;
    } else {
      t18 = $[63];
    }
    if ($[64] !== hasPlaceholder || $[65] !== icon || $[66] !== logo || $[67] !== media?.image || $[68] !== seoLevel || $[69] !== variant) {
      t19 = !logo && !icon && !["quotation", "quotationCentered"].includes(variant) && (media?.image || hasPlaceholder) && <Image className="mb-snug-md FeatureBase__image" image={media?.image} aspectRatio={media?.image?.ratio || "4-3"} objectFit={media?.image?.ratio === "contain" ? "contain" : undefined} objectPosition={variant === "centered" ? "center" : "left"} hasPlaceholder={true} priority={seoLevel === 1} />;
      $[64] = hasPlaceholder;
      $[65] = icon;
      $[66] = logo;
      $[67] = media?.image;
      $[68] = seoLevel;
      $[69] = variant;
      $[70] = t19;
    } else {
      t19 = $[70];
    }
    t15 = cn("FeatureBase__content w-full flex flex-col", spacingClasses.classNameContentSpacing);
    t14 = cn("FeatureBase__text flex flex-col", spacingClasses.classNameHeaderSpacing);
    $[24] = bodySize;
    $[25] = bodyVariant;
    $[26] = buttonSize;
    $[27] = className;
    $[28] = hasPlaceholder;
    $[29] = icon;
    $[30] = inline;
    $[31] = isClickable;
    $[32] = logo;
    $[33] = media?.image;
    $[34] = programmaticButtons;
    $[35] = props.primaryCTA;
    $[36] = props.secondaryCTA;
    $[37] = props.tertiaryCTA;
    $[38] = seoLevel;
    $[39] = subtitle;
    $[40] = t11;
    $[41] = t12;
    $[42] = t13;
    $[43] = title;
    $[44] = titleSize;
    $[45] = titleVariant;
    $[46] = variant;
    $[47] = variantResponsive;
    $[48] = variantSpacing;
    $[49] = hasButtons;
    $[50] = hasTitleGroup;
    $[51] = t14;
    $[52] = t15;
    $[53] = t16;
    $[54] = t17;
    $[55] = t18;
    $[56] = t19;
    $[57] = titleAsLink;
  } else {
    hasButtons = $[49];
    hasTitleGroup = $[50];
    t14 = $[51];
    t15 = $[52];
    t16 = $[53];
    t17 = $[54];
    t18 = $[55];
    t19 = $[56];
    titleAsLink = $[57];
  }
  let t20;
  if ($[71] !== TitleTag || $[72] !== hasTitleGroup || $[73] !== isClickable || $[74] !== props.handleTitleClick || $[75] !== props.primaryCTA || $[76] !== subtitle || $[77] !== title || $[78] !== titleAsLink || $[79] !== titleSize || $[80] !== titleVariant || $[81] !== variant) {
    t20 = hasTitleGroup && <div className="FeatureBase__title-group">{variant !== "stat" && subtitle && <div className="FeatureBase__eyebrow mb-snug-lg">{typeof subtitle === "string" ? <Text prominence={Prominence.Potent} size={Sizes.Medium} variant={Variants.Label}>{subtitle}</Text> : <span className="inline-flex items-center gap-snug-xl">{subtitle}</span>}</div>}{title && titleAsLink && props.primaryCTA && <LinkStyle variant="primary" noUnderline={true} inline={true}><Link href={props.primaryCTA?.link} onClick={props.handleTitleClick}><Text asChild={true} prominence={titleVariant !== "heading" && Prominence.Potent || undefined} size={titleSize as Sizes} variant={titleVariant as Variants}><TitleTag className="FeatureBase__title m-none">{title}</TitleTag></Text></Link></LinkStyle>}{title && !titleAsLink && <Text asChild={true} prominence={titleVariant !== "heading" && Prominence.Potent || undefined} size={titleSize as Sizes} variant={titleVariant as Variants}><TitleTag className="FeatureBase__title m-none">{title}</TitleTag></Text>}{variant === "stat" && subtitle && <Text prominence={Prominence.Potent} size={Sizes.Large} variant={Variants.Label}><div className="FeatureBase__subtitle">{subtitle}</div></Text>}{isClickable && <ArkIcon iconName="arrow-up-right" className="FeatureBase__arrow" />}</div>;
    $[71] = TitleTag;
    $[72] = hasTitleGroup;
    $[73] = isClickable;
    $[74] = props.handleTitleClick;
    $[75] = props.primaryCTA;
    $[76] = subtitle;
    $[77] = title;
    $[78] = titleAsLink;
    $[79] = titleSize;
    $[80] = titleVariant;
    $[81] = variant;
    $[82] = t20;
  } else {
    t20 = $[82];
  }
  let t21;
  if ($[83] !== body || $[84] !== bodySize || $[85] !== bodyVariant) {
    t21 = body && <Text asChild={true} size={bodySize as Sizes} variant={bodyVariant as Variants}><div className={cn("FeatureBase__body")}>{body}</div></Text>;
    $[83] = body;
    $[84] = bodySize;
    $[85] = bodyVariant;
    $[86] = t21;
  } else {
    t21 = $[86];
  }
  let t22;
  if ($[87] !== body || $[88] !== bodySize || $[89] !== media?.image || $[90] !== quotation || $[91] !== subtitle || $[92] !== title || $[93] !== variant) {
    t22 = quotation?.text && <div className={cn("text-left", {
      "my-comfy-sm": title || subtitle || body
    })}><Blockquote quotation={{
        ...quotation,
        image: ["quotation", "quotationCentered"].includes(variant) ? media?.image : quotation.image
      }} size={bodySize} /></div>;
    $[87] = body;
    $[88] = bodySize;
    $[89] = media?.image;
    $[90] = quotation;
    $[91] = subtitle;
    $[92] = title;
    $[93] = variant;
    $[94] = t22;
  } else {
    t22 = $[94];
  }
  let t23;
  if ($[95] !== t14 || $[96] !== t20 || $[97] !== t21 || $[98] !== t22) {
    t23 = <div className={t14}>{t20}{t21}{t22}</div>;
    $[95] = t14;
    $[96] = t20;
    $[97] = t21;
    $[98] = t22;
    $[99] = t23;
  } else {
    t23 = $[99];
  }
  let t24;
  if ($[100] !== alignment || $[101] !== buttonSize || $[102] !== hasButtons || $[103] !== isClickable || $[104] !== programmaticButtons || $[105] !== props || $[106] !== titleAsLink || $[107] !== variant) {
    t24 = hasButtons && !isClickable && !titleAsLink && <div className={cn("FeatureBase__actions")}><FeatureButtons {...props} buttonSize={buttonSize} variant={props.buttonVariant} alignment={alignment || (variant === "centered" ? "center" : "left")} inline={true} isLinks={props.buttonsAsLinks}>{programmaticButtons}</FeatureButtons></div>;
    $[100] = alignment;
    $[101] = buttonSize;
    $[102] = hasButtons;
    $[103] = isClickable;
    $[104] = programmaticButtons;
    $[105] = props;
    $[106] = titleAsLink;
    $[107] = variant;
    $[108] = t24;
  } else {
    t24 = $[108];
  }
  let t25;
  if ($[109] !== t15 || $[110] !== t23 || $[111] !== t24) {
    t25 = <div className={t15}>{t23}{t24}</div>;
    $[109] = t15;
    $[110] = t23;
    $[111] = t24;
    $[112] = t25;
  } else {
    t25 = $[112];
  }
  let t26;
  if ($[113] !== t16 || $[114] !== t17 || $[115] !== t18 || $[116] !== t19 || $[117] !== t25) {
    t26 = <div className={t16}>{t17}{t18}{t19}{t25}</div>;
    $[113] = t16;
    $[114] = t17;
    $[115] = t18;
    $[116] = t19;
    $[117] = t25;
    $[118] = t26;
  } else {
    t26 = $[118];
  }
  return t26;
};